import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import './Grammar.css';
import ProfileMenu from '../ProfileMenu';
import GrammarCompare from './GrammarCompare';
import { GRAMMAR_TAB_LABELS } from '../data/grammarCompareData';
import { getCommonVerbs, verbMeaning } from '../data/verbConcepts';

function highlightMatch(word, query) {
    if (!query) return word;
    const idx = word.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return word;
    return (
        <>
            {word.slice(0, idx)}
            <mark className="gr-highlight">{word.slice(idx, idx + query.length)}</mark>
            {word.slice(idx + query.length)}
        </>
    );
}

function Grammar() {
    const navigate = useNavigate();
    const { t, interfaceLanguage } = useLanguage();
    const [search, setSearch] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [expandedLetters, setExpandedLetters] = useState({});
    const [extraVerbs, setExtraVerbs] = useState([]);
    const [tab, setTab] = useState('verbs');
    const tabLabels = GRAMMAR_TAB_LABELS[interfaceLanguage] || GRAMMAR_TAB_LABELS.en;
    const displayName = localStorage.getItem('vozoly_display_name') || 'U';
    const userInitial = displayName[0].toUpperCase();
    const { activeLanguage } = useUserLanguage();
    const targetLanguage = activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian';
    const commonVerbs = useMemo(() => getCommonVerbs(targetLanguage.toLowerCase()), [targetLanguage]);

    // Meaning of a verb in the interface language. Verbs added from the database have no saved meaning except in English.
    const verbTranslation = (v) => {
        if (v.row) return verbMeaning(v.row, interfaceLanguage, targetLanguage.toLowerCase());
        return interfaceLanguage === 'en' ? v.translation : '···';
    };

    useEffect(() => {
        const loadCachedVerbs = async () => {
            const langCapitalized = targetLanguage.charAt(0).toUpperCase() + targetLanguage.slice(1).toLowerCase();
            const { data } = await supabase
                .from('verb_conjugations')
                .select('infinitive, translation')
                .eq('language', langCapitalized);
            if (data) {
                const known = new Set(commonVerbs.map(v => v.word));
                const extras = data
                    .filter(v => !known.has(v.infinitive.toLowerCase()))
                    .map(v => ({ word: v.infinitive.toLowerCase(), translation: v.translation || '' }));
                setExtraVerbs(extras);
            }
        };
        loadCachedVerbs();
    }, [targetLanguage, commonVerbs]);

    const allVerbs = useMemo(() => {
        const combined = [...commonVerbs, ...extraVerbs];
        const seen = new Set();
        return combined.filter(v => {
            if (seen.has(v.word)) return false;
            seen.add(v.word);
            return true;
        }).sort((a, b) => a.word.localeCompare(b.word));
    }, [extraVerbs, commonVerbs]);

    const goToVerb = (word) => {
        if (!word.trim()) return;
        setShowSuggestions(false);
        navigate(`/verb/${encodeURIComponent(word.trim().toLowerCase())}`);
    };

    const suggestions = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return [];
        return allVerbs.filter(v => v.word.startsWith(q)).slice(0, 8);
    }, [search, allVerbs]);

    const grouped = allVerbs.reduce((acc, v) => {
        const letter = v.word[0].toUpperCase();
        if (!acc[letter]) acc[letter] = [];
        acc[letter].push(v);
        return acc;
    }, {});
    const letters = Object.keys(grouped).sort();

    const toggleLetter = (letter) => {
        setExpandedLetters(prev => ({ ...prev, [letter]: !prev[letter] }));
    };

    return (
        <div className="gr-screen">

            <div className="gr-top-bar">
                <div className="gr-top-row">
                    <div className="gr-title">{t('grammar.title')}</div>
                    <ProfileMenu initial={userInitial} />
                </div>
                <div className="gr-tabs">
                    <button className={`gr-tab ${tab === 'verbs' ? 'active' : ''}`} onClick={() => setTab('verbs')}>{tabLabels.verbs}</button>
                    <button className={`gr-tab ${tab === 'compare' ? 'active' : ''}`} onClick={() => setTab('compare')}>{tabLabels.compare}</button>
                </div>
                <div className="gr-search-wrap" style={tab === 'verbs' ? undefined : { display: 'none' }}>
                    <div className="gr-search">
                        <span className="gr-search-icon">🔍</span>
                        <input
                            className="gr-search-input"
                            type="text"
                            placeholder={t('grammar.searchPlaceholder')}
                            value={search}
                            onChange={e => { setSearch(e.target.value); setShowSuggestions(true); }}
                            onFocus={() => setShowSuggestions(true)}
                            onKeyDown={e => e.key === 'Enter' && goToVerb(search)}
                        />
                        {search && (
                            <button className="gr-search-clear" onClick={() => { setSearch(''); setShowSuggestions(false); }}>✕</button>
                        )}
                    </div>
                    {showSuggestions && suggestions.length > 0 && (
                        <div className="gr-suggestions">
                            {suggestions.map(v => (
                                <div key={v.word} className="gr-suggestion-row" onClick={() => goToVerb(v.word)}>
                                    <div className="gr-suggestion-word">{highlightMatch(v.word, search)}</div>
                                    {verbTranslation(v) && <div className="gr-suggestion-translation">{verbTranslation(v)}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {search && tab === 'verbs' && (
                    <button className="gr-search-go" onClick={() => goToVerb(search)}>
                        {t('grammar.lookUp')} "{search}" →
                    </button>
                )}
            </div>

            <div className="gr-intro" style={tab === 'verbs' ? undefined : { display: 'none' }}>
                {t('grammar.intro')} <strong>{t('grammar.introBold')}</strong> {t('grammar.introEnd')}
            </div>

            <div className="gr-list" style={tab === 'verbs' ? undefined : { display: 'none' }}>
                {letters.map(letter => {
                    const isOpen = !!expandedLetters[letter];
                    return (
                        <div key={letter} className="gr-letter-group">
                            <div className="gr-letter-header" onClick={() => toggleLetter(letter)}>
                                <span>{letter}</span>
                                <span className="gr-letter-count">{grouped[letter].length} {t('grammar.verbs')}</span>
                                <span className={`gr-letter-chevron ${isOpen ? 'open' : ''}`}>▾</span>
                            </div>
                            {isOpen && grouped[letter].map(v => (
                                <div key={v.word} className="gr-verb-row" onClick={() => goToVerb(v.word)}>
                                    <div className="gr-verb-info">
                                        <div className="gr-verb-word">{v.word}</div>
                                        <div className="gr-verb-translation">{verbTranslation(v)}</div>
                                    </div>
                                    <span className="gr-verb-arrow">›</span>
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>

            {tab === 'compare' && <GrammarCompare />}

            <div style={{ height: '80px' }}></div>

            <div className="gr-bottom-nav">
                <button className="gr-nav-item" onClick={() => navigate('/home')}>
                    <div className="gr-nav-icon">🏠</div>
                    <div className="gr-nav-label">{t('nav.home')}</div>
                </button>
                <button className="gr-nav-item" onClick={() => navigate('/topics')}>
                    <div className="gr-nav-icon">📚</div>
                    <div className="gr-nav-label">{t('nav.topics')}</div>
                </button>
                <button className="gr-nav-item active">
                    <div className="gr-nav-icon nav-icon-grammar">📐</div>
                    <div className="gr-nav-label">{t('nav.grammar')}</div>
                    <div className="gr-nav-dot"></div>
                </button>
                <button className="gr-nav-item" onClick={() => navigate('/wordbank')}>
                    <div className="gr-nav-icon">📒</div>
                    <div className="gr-nav-label">{t('nav.wordbank')}</div>
                </button>
                <button className="gr-nav-item" onClick={() => navigate('/games')}>
                    <div className="gr-nav-icon">🎮</div>
                    <div className="gr-nav-label">{t('nav.games')}</div>
                </button>
            </div>

        </div>
    );
}

export default Grammar;